import { useState } from 'react'
import { catalogApi } from '../lib/api'
import type { ExpenseCategory } from '../lib/db'
import { useApp } from '../lib/store'
import { Sheet } from './Sheet'

/**
 * Biểu tượng cho nút chi phí. Chọn theo thứ quán nhỏ hay mua chứ không phải theo
 * danh mục kế toán — chủ quán tìm bằng mắt, thấy con heo là bấm.
 */
const EMOJIS = [
  '🥩', '🍗', '🐟', '🥬', '🍚', '🧂', '☕', '🥛', '🍵', '🧊',
  '🍬', '🥤', '🥢', '🍋', '📦', '🔥', '💡', '🏠', '👤', '🚚',
  '🧽', '🛠️', '📱', '➕',
]

interface Props {
  categories: ExpenseCategory[]
  onClose: () => void
  onChanged: () => void | Promise<void>
}

export function ExpenseCategoryManager({ categories, onClose, onChanged }: Props) {
  const say = useApp((s) => s.say)

  const [order, setOrder] = useState(categories)
  const [editing, setEditing] = useState<ExpenseCategory | null>(null)
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('🧺')
  const [busy, setBusy] = useState(false)

  function startNew() {
    setEditing({ id: '', name: '', emoji: '🧺', sortOrder: order.length })
    setName('')
    setEmoji('🧺')
  }

  function startEdit(c: ExpenseCategory) {
    setEditing(c)
    setName(c.name)
    setEmoji(c.emoji)
  }

  async function save() {
    if (!name.trim()) {
      say('Chưa đặt tên khoản chi')
      return
    }
    setBusy(true)
    try {
      await catalogApi.saveExpenseCategory({
        id: editing?.id || undefined,
        name: name.trim(),
        emoji,
      })
      setEditing(null)
      await onChanged()
      say('Đã lưu')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  async function remove(c: ExpenseCategory) {
    setBusy(true)
    try {
      await catalogApi.deleteExpenseCategory(c.id)
      setOrder((list) => list.filter((x) => x.id !== c.id))
      setEditing(null)
      await onChanged()
      say(`Đã bỏ nút ${c.name}`)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa bỏ được')
    }
    setBusy(false)
  }

  async function move(index: number, delta: number) {
    const next = [...order]
    const target = index + delta
    if (target < 0 || target >= next.length) return

    const a = next[index]
    const b = next[target]
    if (!a || !b) return

    next[index] = b
    next[target] = a
    setOrder(next)

    try {
      await catalogApi.reorderExpenseCategories(next.map((c) => c.id))
      await onChanged()
    } catch {
      setOrder(order)
      say('Cần có mạng để đổi thứ tự')
    }
  }

  if (editing) {
    const isNew = !editing.id
    return (
      <Sheet title={isNew ? 'Thêm nút chi' : editing.name} onClose={() => setEditing(null)}>
        <label className="field">
          <span>Mua gì</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Thịt heo"
            maxLength={80}
            autoFocus
          />
        </label>

        <div className="field">
          <span>Biểu tượng</span>
          <div className="emoji-grid">
            {EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                className="pick-icon"
                aria-pressed={e === emoji}
                onClick={() => setEmoji(e)}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="sheet-actions">
          {!isNew && (
            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={() => remove(editing)}
              disabled={busy}
            >
              Bỏ nút
            </button>
          )}
          <button type="button" className="btn btn-brand grow" onClick={save} disabled={busy}>
            {busy ? 'Đang lưu…' : 'Lưu'}
          </button>
        </div>

        {!isNew && (
          <p className="note">
            Bỏ nút không xoá phiếu chi cũ — mỗi phiếu đã chụp lại tên và biểu tượng lúc ghi.
          </p>
        )}
      </Sheet>
    )
  }

  return (
    <Sheet title="Nút chi phí" onClose={onClose}>
      <p className="sheet-note">
        Thứ tự ở đây là thứ tự nút lúc nhập hàng — thứ hay mua thì đẩy lên trên.
      </p>

      <div className="list">
        {order.map((c, i) => (
          <div key={c.id} className="group-row">
            <div className="group-move">
              <button
                type="button"
                onClick={() => void move(i, -1)}
                disabled={i === 0}
                aria-label={`Đưa ${c.name} lên trên`}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => void move(i, 1)}
                disabled={i === order.length - 1}
                aria-label={`Đưa ${c.name} xuống dưới`}
              >
                ↓
              </button>
            </div>

            <button type="button" className="group-name" onClick={() => startEdit(c)}>
              <b>
                {c.emoji} {c.name}
              </b>
            </button>
          </div>
        ))}
      </div>

      <div className="sheet-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Đóng
        </button>
        <button type="button" className="btn btn-brand grow" onClick={startNew}>
          + Thêm nút
        </button>
      </div>
    </Sheet>
  )
}
